'use client';

import { motion, useReducedMotion, useSpring, useMotionTemplate } from 'framer-motion';
import { Moon, Crown, Heart, Sparkles } from 'lucide-react';

const EASE = [0.16, 1, 0.3, 1] as const;

// ─── Era data ────────────────────────────────────────────────────────────────

const ERAS = [
  {
    n: '1.0',
    year: '1985 →',
    label: 'Spreadsheets',
    sub: 'The Excel era',
    desc: 'Manual everything. Version control by filename.',
    accent: '#9CA3AF',
  },
  {
    n: '2.0',
    year: '2005 →',
    label: 'BI Tools',
    sub: 'The dashboard era',
    desc: 'Pretty charts on stale data, but still manual underneath.',
    accent: '#A78BFA',
  },
  {
    n: '3.0',
    year: 'Now',
    label: 'AI-Native',
    sub: 'The Blueberries era',
    desc: 'AI agents own the pipeline. Real-time. Audit-grade. No spreadsheets.',
    accent: '#6D28D9',
    active: true,
  },
];

// ─── Payoff data ─────────────────────────────────────────────────────────────

const PAYOFFS = [
  {
    icon: Moon,
    title: 'Sleep at night.',
    desc: 'Audit-grade lineage on every number. Every figure traces back to source. No more 2am Slack pings asking which spreadsheet is the latest.',
  },
  {
    icon: Crown,
    title: 'A seat at the table.',
    desc: 'Stop being the data janitor. AI handles the plumbing so you handle strategy. CFOs go from "make the spreadsheet work" to "drive the business."',
  },
  {
    icon: Heart,
    title: 'Your weekends back.',
    desc: 'No more month-end death marches. No more late-night reconciliation. Real-time forecasts mean Friday actually means Friday.',
  },
];

// ═════════════════════════════════════════════════════════════════════════════

export default function CategorySection() {
  return (
    <section id="impact" className="relative pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden" style={{ backgroundColor: '#FBFAFF' }}>
      {/* Subtle grid texture only — top-anchored bloom removed so backgrounds line up between sections */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(76,29,149,1) 1px, transparent 1px), linear-gradient(90deg, rgba(76,29,149,1) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />

      <div className="relative container mx-auto max-w-7xl px-6">
        {/* ═══════════════════ PART A — THE CATEGORY ═══════════════════ */}

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-center max-w-4xl mx-auto"
        >
          <div className="inline-flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-purple-700/80 font-semibold">
            <span className="h-px w-8 bg-purple-300" />
            The new category
            <span className="h-px w-8 bg-purple-300" />
          </div>
          <h2 className="mt-6 text-4xl md:text-6xl font-semibold tracking-[-0.035em] text-gray-900 leading-[1.05]">
            FP&amp;A 1.0 was spreadsheets.{' '}
            <span className="text-gray-400 font-light">FP&amp;A 2.0 was BI.</span>{' '}
            <br className="hidden md:block" />
            FP&amp;A 3.0 is{' '}
            <span className="bg-gradient-to-br from-[#6D28D9] via-[#7C3AED] to-[#A78BFA] bg-clip-text text-transparent">
              AI-native.
            </span>
          </h2>
        </motion.div>

        {/* Era timeline cards */}
        <div className="mt-16 md:mt-20 relative">
          <div className="grid md:grid-cols-3 gap-4 md:gap-6 relative">
            {ERAS.map((era, i) => (
              <EraCard key={era.n} era={era} i={i} />
            ))}
          </div>
        </div>

        {/* Welcome to 3.0 closing */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ delay: 0.6, duration: 0.7, ease: EASE }}
          className="mt-12 flex items-center justify-center gap-3"
        >
          <Sparkles className="h-6 w-6 text-purple-600" strokeWidth={2.2} />
          <span className="text-3xl md:text-4xl font-semibold tracking-tight text-gray-900">
            Welcome to{' '}
            <span className="text-[#7C3AED]">FP&amp;A 3.0.</span>
          </span>
        </motion.div>

        {/* Connector — flows the era story straight into the payoff, one narrative */}
        <div className="my-6 md:my-8 flex justify-center">
          <motion.div
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            style={{ transformOrigin: 'top' }}
            className="h-10 w-px bg-gradient-to-b from-[#6D28D9] via-purple-300 to-transparent"
          />
        </div>

        {/* ═══════════════════ PART B — WHAT YOU GET BACK ═══════════════════ */}

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-purple-700/80 font-semibold">
            <span className="h-px w-8 bg-purple-300" />
            What you get back
            <span className="h-px w-8 bg-purple-300" />
          </div>
          <h3 className="mt-6 text-3xl md:text-5xl font-semibold tracking-[-0.035em] text-gray-900 leading-[1.05]">
            Beyond the numbers:{' '}
            <span className="italic text-gray-500">what changes for you.</span>
          </h3>
        </motion.div>

        <div className="mt-14 md:mt-16 grid md:grid-cols-3 gap-5 md:gap-6">
          {PAYOFFS.map((p, i) => (
            <TiltCard key={p.title}>
              <PayoffCard payoff={p} i={i} />
            </TiltCard>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Era card ────────────────────────────────────────────────────────────────

function EraCard({ era, i }: { era: typeof ERAS[number]; i: number }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: era.active ? -14 : 0, scale: era.active ? 1.03 : 1 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ delay: 0.15 + i * 0.1, duration: 0.7, ease: EASE }}
      className={`relative rounded-3xl border overflow-hidden p-7 md:p-8 ${
        era.active
          ? 'z-10 border-purple-200 bg-gradient-to-br from-purple-50/70 via-white to-violet-50/50 shadow-[0_40px_100px_-30px_rgba(76,29,149,0.55)]'
          : 'border-gray-100 bg-white/70 backdrop-blur'
      }`}
    >
      {/* Active glow — gently breathes */}
      {era.active && (
        <motion.div
          aria-hidden
          animate={reduce ? undefined : { opacity: [0.55, 0.9, 0.55], scale: [1, 1.12, 1] }}
          transition={reduce ? undefined : { duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-20 -right-20 h-44 w-44 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.4), transparent 70%)' }}
        />
      )}

      {/* Timeline node dot */}
      <div className="hidden md:flex absolute top-[64px] left-1/2 -translate-x-1/2 z-10">
        <span
          className={`relative flex h-4 w-4 items-center justify-center rounded-full border-2 bg-white ${
            era.active ? 'border-[#6D28D9]' : 'border-gray-300'
          }`}
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: era.active ? '#6D28D9' : '#D1D5DB' }}
          />
          {era.active && (
            <span className="absolute inset-0 rounded-full border-2 border-purple-400 animate-ping" />
          )}
        </span>
      </div>

      <div className="relative pt-12 md:pt-20">
        {/* Era number */}
        <div className="flex items-baseline justify-between mb-1">
          <span
            className={`inline-block text-[64px] md:text-[80px] font-semibold tracking-[-0.05em] leading-[1.12] pb-[0.08em] ${
              era.active ? 'text-transparent bg-clip-text bg-gradient-to-br from-[#4C1D95] via-[#6D28D9] to-[#A78BFA]' : 'text-gray-300'
            }`}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {era.n}
          </span>
          <span className={`text-[10px] tracking-[0.22em] uppercase font-semibold ${era.active ? 'text-purple-600' : 'text-gray-400'}`}>
            {era.year}
          </span>
        </div>

        {/* Visual */}
        <div className="mt-5 mb-5">
          <EraVisual variant={era.n} />
        </div>

        {/* Label + sub */}
        <h3 className={`text-2xl md:text-3xl font-semibold tracking-tight ${era.active ? 'text-gray-900' : 'text-gray-500'}`}>
          {era.label}
        </h3>
        <div className={`mt-1 text-[11px] tracking-[0.22em] uppercase font-semibold ${era.active ? 'text-purple-600' : 'text-gray-400'}`}>
          {era.sub}
        </div>

        {/* Description */}
        <p className={`mt-4 text-[15px] leading-[1.6] ${era.active ? 'text-gray-700' : 'text-gray-500'}`}>
          {era.desc}
        </p>

        {/* Active badge */}
        {era.active && (
          <div className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-purple-100/60 px-2.5 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-purple-700">
              You are here
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Era visuals (3 distinct mini scenes) ─────────────────────────────────────

function EraVisual({ variant }: { variant: string }) {
  if (variant === '1.0') {
    // Spreadsheet grid - faded, manual
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-2 overflow-hidden">
        <div className="flex items-center gap-1 mb-1.5 pb-1.5 border-b border-gray-100">
          <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
          <span className="text-[8px] font-mono text-gray-400">Budget_FINAL_v14.xlsx</span>
        </div>
        <div className="grid grid-cols-4 gap-px bg-gray-100 rounded overflow-hidden">
          {Array.from({ length: 20 }).map((_, k) => (
            <div
              key={k}
              className={`h-3.5 ${
                k === 0 ? 'bg-gray-200' :
                k % 5 === 0 ? 'bg-emerald-50' :
                k === 7 || k === 13 ? 'bg-red-50' : 'bg-white'
              }`}
            />
          ))}
        </div>
      </div>
    );
  }

  if (variant === '2.0') {
    // BI dashboard - sparkline + bars, slightly more polished
    return (
      <div className="rounded-xl border border-purple-100/60 bg-white p-3 overflow-hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[8px] tracking-wider uppercase text-gray-400 font-semibold">Dashboard · Q3</span>
          <span className="text-[8px] text-gray-300 font-mono">refreshed 14d ago</span>
        </div>
        <svg viewBox="0 0 200 50" className="w-full h-10">
          <defs>
            <linearGradient id="bi-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#A78BFA" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#A78BFA" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0,38 C30,32 50,22 80,28 C110,34 130,16 160,20 C180,22 195,14 200,12 L200,50 L0,50 Z" fill="url(#bi-fill)" />
          <path d="M0,38 C30,32 50,22 80,28 C110,34 130,16 160,20 C180,22 195,14 200,12" fill="none" stroke="#A78BFA" strokeWidth="1.5" />
        </svg>
      </div>
    );
  }

  // 3.0 — AI-native, alive
  return (
    <div className="relative rounded-xl border border-purple-200 bg-gradient-to-br from-white via-purple-50/40 to-violet-50/40 p-3 overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <span className="inline-flex items-center gap-1">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          <span className="text-[8px] tracking-wider uppercase text-purple-600 font-bold">Live · 2s ago</span>
        </span>
        <Sparkles className="h-3 w-3 text-purple-600" strokeWidth={2.2} />
      </div>
      <svg viewBox="0 0 200 50" className="w-full h-10 overflow-visible">
        <defs>
          <linearGradient id="ai-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#A78BFA" />
          </linearGradient>
          <path id="ai-flow" d="M0,38 C30,32 50,18 80,22 C110,26 130,8 160,10 C180,11 195,5 200,3" fill="none" />
        </defs>
        <use href="#ai-flow" stroke="url(#ai-line)" strokeWidth="2" strokeLinecap="round" />
        {/* Flowing particles along the path */}
        {[0, 0.6, 1.2].map((delay, k) => (
          <circle key={k} r="2.5" fill="#7C3AED">
            <animateMotion dur="2.4s" repeatCount="indefinite" begin={`${delay}s`}>
              <mpath href="#ai-flow" />
            </animateMotion>
          </circle>
        ))}
        <circle cx="200" cy="3" r="2.5" fill="#6D28D9" />
      </svg>
    </div>
  );
}

// ─── Tilt wrapper — 3D cursor lean + moving glare ─────────────────────────────

function TiltCard({ children }: { children: React.ReactNode }) {
  const reduce = useReducedMotion();
  const rx = useSpring(0, { stiffness: 150, damping: 18 });
  const ry = useSpring(0, { stiffness: 150, damping: 18 });
  const gx = useSpring(50, { stiffness: 120, damping: 20 });
  const gy = useSpring(50, { stiffness: 120, damping: 20 });
  const glare = useMotionTemplate`radial-gradient(420px circle at ${gx}% ${gy}%, rgba(196,181,253,0.22), transparent 56%)`;

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    ry.set(nx * 9);
    rx.set(-ny * 9);
    gx.set(50 + nx * 60);
    gy.set(50 + ny * 60);
  };
  const onLeave = () => { rx.set(0); ry.set(0); gx.set(50); gy.set(50); };

  return (
    <div className="h-full [perspective:1200px]" onMouseMove={onMove} onMouseLeave={onLeave}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: 'preserve-3d' }} className="relative h-full">
        {children}
        {!reduce && (
          <motion.div aria-hidden style={{ background: glare }} className="pointer-events-none absolute inset-0 z-20 rounded-3xl" />
        )}
      </motion.div>
    </div>
  );
}

// ─── Payoff card ─────────────────────────────────────────────────────────────

function PayoffCard({ payoff, i }: { payoff: typeof PAYOFFS[number]; i: number }) {
  const Icon = payoff.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ delay: i * 0.1, duration: 0.7, ease: EASE }}
      className="group relative h-full rounded-3xl border border-purple-100/80 bg-white p-8 md:p-9 overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_30px_80px_-30px_rgba(109,40,217,0.4)] transition-all"
    >
      {/* Top accent line */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, #A78BFA, transparent)' }}
      />
      {/* Hover glow */}
      <div
        aria-hidden
        className="absolute -top-16 -right-16 h-40 w-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.35), transparent 70%)' }}
      />

      <div className="relative">
        {/* Icon */}
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100/60 border border-purple-200/50 transition-transform group-hover:scale-110 group-hover:-rotate-3">
          <Icon className="h-5 w-5 text-purple-600" strokeWidth={2} />
        </div>

        {/* Title — editorial, italic-ish weight */}
        <h4 className="mt-6 text-2xl md:text-3xl font-semibold tracking-[-0.02em] text-gray-900">
          {payoff.title}
        </h4>

        {/* Description */}
        <p className="mt-3 text-[15px] text-gray-600 leading-[1.65]">{payoff.desc}</p>
      </div>
    </motion.div>
  );
}
